import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { adminApi } from '@/api/admin'

export const useAdminStore = defineStore('admin', () => {
  // 状态
  const adminInfo = ref(null)
  const token = ref(sessionStorage.getItem('admin_token') || localStorage.getItem('admin_token') || '')
  const permissions = ref([])
  const loading = ref(false)
  const rememberMe = ref(localStorage.getItem('admin_remember_me') === '1')
  const lastActive = ref(Number(sessionStorage.getItem('admin_last_active') || Date.now()))
  const sessionTimeoutMinutes = Number(import.meta.env.VITE_SESSION_TIMEOUT_MINUTES || 120)
  let sessionTimer = null
  let activityListeners = [] // 存储事件监听器
  let isLoggingOut = false // 防止重复登出

  // 计算属性
  const isLoggedIn = computed(() => !!token.value && !!adminInfo.value)
  const adminName = computed(() => adminInfo.value?.name || '')
  const adminRole = computed(() => adminInfo.value?.role || '')

  const getStorage = () => (rememberMe.value ? localStorage : sessionStorage)

  // 方法
  const setRememberMe = (value) => {
    rememberMe.value = !!value
    localStorage.setItem('admin_remember_me', rememberMe.value ? '1' : '0')
  }

  const persistSession = () => {
    const storage = getStorage()
    if (token.value) storage.setItem('admin_token', token.value)
    if (adminInfo.value) storage.setItem('admin_info', JSON.stringify(adminInfo.value))
    sessionStorage.setItem('admin_last_active', String(Date.now()))
  }

  const login = async (credentials) => {
    try {
      loading.value = true
      // 支持从 credentials.rememberMe 透传记住状态
      if (typeof credentials.rememberMe === 'boolean') {
        setRememberMe(credentials.rememberMe)
      }

      const response = await adminApi.login(credentials)
      
      if (response.success) {
        token.value = response.data.token
        adminInfo.value = response.data.admin
        permissions.value = response.data.permissions || []
        
        // 清理两端存储，避免残留
        localStorage.removeItem('admin_token'); sessionStorage.removeItem('admin_token')
        localStorage.removeItem('admin_info'); sessionStorage.removeItem('admin_info')

        // 保存到选定存储
        persistSession()
        
        return { success: true }
      } else {
        return { success: false, message: response.message }
      }
    } catch (error) {
      console.error('登录失败:', error)
      return { success: false, message: error.message || '登录失败' }
    } finally {
      loading.value = false
    }
  }

  const logout = async () => {
    // 防止重复登出
    if (isLoggingOut) return
    isLoggingOut = true

    try {
      // 清除本地状态
      token.value = ''
      adminInfo.value = null
      permissions.value = []
      
      // 清除本地存储
      localStorage.removeItem('admin_token')
      localStorage.removeItem('admin_info')
      sessionStorage.removeItem('admin_token')
      sessionStorage.removeItem('admin_info')
      sessionStorage.removeItem('admin_last_active')

      // 停止会话监听
      stopSessionWatch()
    } finally {
      isLoggingOut = false
    }
  }

  const verifyToken = async () => {
    try {
      const response = await adminApi.verifyToken()
      if (response.success) {
        adminInfo.value = response.data.admin
        permissions.value = response.data.permissions || []
        persistSession()
        return true
      } else {
        throw new Error('Token验证失败')
      }
    } catch (error) {
      // Token无效，清除本地状态
      token.value = ''
      adminInfo.value = null
      permissions.value = []
      localStorage.removeItem('admin_token')
      localStorage.removeItem('admin_info')
      sessionStorage.removeItem('admin_token')
      sessionStorage.removeItem('admin_info')
      throw error
    }
  }

  const initAdmin = () => {
    // 从两端存储恢复管理员信息
    const savedAdminInfo = sessionStorage.getItem('admin_info') || localStorage.getItem('admin_info')
    if (savedAdminInfo) {
      try {
        adminInfo.value = JSON.parse(savedAdminInfo)
      } catch (error) {
        console.error('解析管理员信息失败:', error)
        localStorage.removeItem('admin_info')
        sessionStorage.removeItem('admin_info')
      }
    }
  }

  const updateAdminInfo = (newInfo) => {
    adminInfo.value = { ...adminInfo.value, ...newInfo }
    const storage = getStorage()
    storage.setItem('admin_info', JSON.stringify(adminInfo.value))
  }

  const hasPermission = (permission) => {
    return permissions.value.includes(permission) || adminRole.value === 'super_admin'
  }

  const updateActivity = () => {
    lastActive.value = Date.now()
    sessionStorage.setItem('admin_last_active', String(lastActive.value))
  }

  const isSessionExpired = () => {
    const last = Number(sessionStorage.getItem('admin_last_active') || lastActive.value || Date.now())
    const elapsedMinutes = (Date.now() - last) / 60000
    return elapsedMinutes > sessionTimeoutMinutes
  }

  const startSessionWatch = () => {
    if (sessionTimer) return
    
    updateActivity()
    
    // 创建活动监听器
    const onActivity = () => updateActivity()
    const listeners = [
      { event: 'click', handler: onActivity },
      { event: 'keydown', handler: onActivity },
      { event: 'mousemove', handler: onActivity }
    ]
    
    // 添加监听器
    listeners.forEach(({ event, handler }) => {
      window.addEventListener(event, handler)
      activityListeners.push({ event, handler })
    })
    
    // 会话检查定时器
    sessionTimer = setInterval(async () => {
      if (token.value && isSessionExpired()) {
        console.log('会话已过期，自动退出登录')
        await logout()
        // 发送事件而非直接跳转，让路由守卫处理
        window.dispatchEvent(new CustomEvent('session-expired'))
      }
    }, 60000)
  }

  const stopSessionWatch = () => {
    // 清除定时器
    if (sessionTimer) {
      clearInterval(sessionTimer)
      sessionTimer = null
    }
    
    // 移除活动监听器
    activityListeners.forEach(({ event, handler }) => {
      window.removeEventListener(event, handler)
    })
    activityListeners = []
  }

  // 校验是否具备任一权限
  const hasAnyPermission = (requiredPermissions = []) => {
    if (!Array.isArray(requiredPermissions) || requiredPermissions.length === 0) return true
    if (adminRole.value === 'super_admin') return true
    return requiredPermissions.some((p) => permissions.value.includes(p))
  }

  return {
    // 状态
    adminInfo,
    token,
    permissions,
    loading,
    rememberMe,
    
    // 计算属性
    isLoggedIn,
    adminName,
    adminRole,
    
    // 方法
    login,
    logout,
    verifyToken,
    initAdmin,
    updateAdminInfo,
    hasPermission,
    hasAnyPermission,
    setRememberMe,
    startSessionWatch,
    stopSessionWatch,
    updateActivity,
    isSessionExpired
  }
}) 